import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseUsd, usd } from '../api/client';
import {
  type Promotion,
  useCreatePromotion,
  usePromotionGrants,
  usePromotions,
  useRevokeGrant,
  useUpdatePromotion,
} from '../api/hooks';
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Input,
  Loading,
  PageHeader,
  Panel,
  Table,
  Td,
  Th,
} from '../components/ui';
import { useApiError } from '../i18n/useApiError';

const TONE = {
  active: 'success',
  paused: 'warning',
  ended: 'neutral',
  draft: 'neutral',
} as const;

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function toIso(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

/**
 * Parking-credit campaigns.
 *
 * The screen is built around one honest number: `redeemedCents`. Handing out
 * credit costs nothing until a driver actually parks on it, so "granted" is a
 * vanity figure and "spent" is the one an owner should watch.
 */
export function PromotionsPage({ canEdit }: { canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const describeError = useApiError();
  const promotions = usePromotions();
  const create = useCreatePromotion();
  const update = useUpdatePromotion();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Promotion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deviceQuery, setDeviceQuery] = useState('');
  const [deviceSearch, setDeviceSearch] = useState('');

  const [name, setName] = useState('');
  const [grant, setGrant] = useState('20.00');
  const [budget, setBudget] = useState('1000.00');
  const [ttl, setTtl] = useState('30');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');

  const openForm = (promotion?: Promotion) => {
    setEditing(promotion ?? null);
    setName(promotion?.name ?? '');
    setGrant(promotion ? (promotion.grantCents / 100).toFixed(2) : '20.00');
    setBudget(promotion ? (promotion.totalBudgetCents / 100).toFixed(2) : '1000.00');
    setTtl(promotion?.creditTtlDays?.toString() ?? (promotion ? '' : '30'));
    setStartsAt(toLocalInput(promotion?.startsAt ?? null));
    setEndsAt(toLocalInput(promotion?.endsAt ?? null));
    setError(null);
    setOpen(true);
  };

  const closeForm = () => {
    setOpen(false);
    setEditing(null);
  };

  const submit = async () => {
    setError(null);
    const grantCents = parseUsd(grant);
    const totalBudgetCents = parseUsd(budget);
    const creditTtlDays = ttl ? Number(ttl) : null;
    if (!Number.isInteger(grantCents) || grantCents <= 0 || totalBudgetCents < grantCents) {
      setError(t('promotions.invalidAmounts'));
      return;
    }
    if (creditTtlDays !== null && (!Number.isInteger(creditTtlDays) || creditTtlDays <= 0)) {
      setError(t('promotions.invalidTtl'));
      return;
    }
    if (startsAt && endsAt && new Date(endsAt) <= new Date(startsAt)) {
      setError(t('promotions.invalidWindow'));
      return;
    }
    const input = {
      name: name.trim(),
      grantCents,
      totalBudgetCents,
      creditTtlDays,
      startsAt: toIso(startsAt),
      endsAt: toIso(endsAt),
    };
    try {
      if (editing) await update.mutateAsync({ id: editing.id, ...input });
      else await create.mutateAsync(input);
      closeForm();
    } catch (e) {
      setError(describeError(e, editing ? 'promotions.updateFailed' : 'promotions.createFailed'));
    }
  };

  const setStatus = async (promotion: Promotion, status: Promotion['status']) => {
    setError(null);
    try {
      await update.mutateAsync({ id: promotion.id, status });
    } catch (e) {
      setError(describeError(e, 'promotions.updateFailed'));
    }
  };

  if (promotions.isPending) return <Loading />;

  const list = promotions.data?.promotions ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('promotions.title')}
        hint={t('promotions.hint')}
        actions={
          canEdit ? (
            <Button variant="primary" onClick={() => (open && !editing ? closeForm() : openForm())}>
              {t('promotions.new')}
            </Button>
          ) : undefined
        }
      />

      {error ? <ErrorNote message={error} /> : null}

      {open ? (
        <Card className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-[15px] font-bold">
              {t(editing ? 'promotions.editTitle' : 'promotions.newTitle')}
            </h2>
            {editing ? (
              <Badge tone={TONE[editing.status]}>{t(`promotions.status.${editing.status}`)}</Badge>
            ) : null}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label={t('promotions.name')}>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label={t('promotions.grant')}>
              <Input className="num" value={grant} onChange={(e) => setGrant(e.target.value)} />
            </Field>
            <Field label={t('promotions.budget')}>
              <Input className="num" value={budget} onChange={(e) => setBudget(e.target.value)} />
            </Field>
            <Field label={t('promotions.ttl')} hint={t('promotions.ttlNever')}>
              <Input className="num" value={ttl} onChange={(e) => setTtl(e.target.value)} />
            </Field>
            <Field label={t('promotions.startsAt')} hint={t('promotions.localTime')}>
              <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </Field>
            <Field label={t('promotions.endsAt')} hint={t('promotions.localTime')}>
              <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </Field>
          </div>
          <div className="flex gap-2">
            <Button
              variant="dark"
              onClick={() => void submit()}
              disabled={create.isPending || update.isPending || !name.trim()}
            >
              {t(editing ? 'promotions.save' : 'promotions.create')}
            </Button>
            <Button onClick={closeForm}>{t('common.cancel')}</Button>
          </div>
        </Card>
      ) : null}

      {list.length === 0 ? (
        <Empty>{t('promotions.empty')}</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('promotions.colName')}</Th>
              <Th>{t('promotions.colStatus')}</Th>
              <Th align="right">{t('promotions.colGrant')}</Th>
              <Th align="right">{t('promotions.colBudget')}</Th>
              <Th align="right">{t('promotions.colGranted')}</Th>
              <Th align="right">{t('promotions.colRedeemed')}</Th>
              <Th align="right">{t('promotions.colDevices')}</Th>
              {canEdit ? (
                <Th align="right" sticky>
                  {t('common.actions')}
                </Th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {list.map((promotion) => (
              <tr key={promotion.id}>
                <Td strong>{promotion.name}</Td>
                <Td>
                  <Badge tone={TONE[promotion.status]}>{t(`promotions.status.${promotion.status}`)}</Badge>
                </Td>
                <Td align="right" mono>
                  {usd(promotion.grantCents)}
                </Td>
                <Td align="right" mono>
                  {usd(promotion.totalBudgetCents)}
                </Td>
                <Td align="right" mono>
                  {usd(promotion.grantedCents)}
                </Td>
                <Td align="right" mono strong>
                  {usd(promotion.redeemedCents)}
                </Td>
                <Td align="right" mono>
                  {promotion.grantCount}
                </Td>
                {canEdit ? (
                  <Td align="right" sticky>
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" onClick={() => openForm(promotion)}>
                        {t('promotions.edit')}
                      </Button>
                      {promotion.status === 'active' ? (
                        <Button variant="ghost" onClick={() => void setStatus(promotion, 'paused')}>
                          {t('promotions.pause')}
                        </Button>
                      ) : promotion.status !== 'ended' ? (
                        <Button variant="ghost" onClick={() => void setStatus(promotion, 'active')}>
                          {t('promotions.activate')}
                        </Button>
                      ) : null}
                      {promotion.status !== 'ended' ? (
                        <Button variant="ghost" onClick={() => void setStatus(promotion, 'ended')}>
                          {t('promotions.end')}
                        </Button>
                      ) : null}
                    </div>
                  </Td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Panel>{t('promotions.panel')}</Panel>

      <div className="flex flex-col gap-3">
        <h3 className="text-[13px] font-bold">{t('promotions.grantsTitle')}</h3>
        <p className="text-[11px] text-muted">{t('promotions.grantsHint')}</p>
        <div className="flex items-end gap-2">
          <div className="w-72">
            <Field label={t('promotions.deviceSearch')}>
              <Input
                className="num"
                value={deviceQuery}
                onChange={(e) => setDeviceQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setDeviceSearch(deviceQuery.trim());
                }}
              />
            </Field>
          </div>
          <Button variant="dark" onClick={() => setDeviceSearch(deviceQuery.trim())}>
            {t('common.search')}
          </Button>
          <Button
            onClick={() => {
              setDeviceQuery('');
              setDeviceSearch('');
            }}
          >
            {t('promotions.clear')}
          </Button>
        </div>

        <GrantsTable deviceId={deviceSearch || undefined} canEdit={canEdit} locale={i18n.language} />
      </div>
    </div>
  );
}

function GrantsTable({ deviceId, canEdit, locale }: { deviceId?: string; canEdit: boolean; locale: string }) {
  const { t } = useTranslation();
  const grants = usePromotionGrants({ deviceId });
  const revoke = useRevokeGrant();

  if (grants.isPending) return <Loading />;
  const rows = grants.data?.grants ?? [];
  if (rows.length === 0) return <Empty>{t('promotions.grantsEmpty')}</Empty>;

  return (
    <Table>
      <thead>
        <tr>
          <Th>{t('promotions.colDevice')}</Th>
          <Th>{t('promotions.colUser')}</Th>
          <Th align="right">{t('promotions.colAmount')}</Th>
          <Th align="right">{t('promotions.colBalance')}</Th>
          <Th>{t('promotions.colPlatform')}</Th>
          <Th>{t('promotions.colGrantedAt')}</Th>
          <Th>{t('promotions.colExpiry')}</Th>
          <Th>{t('promotions.colStatus')}</Th>
          {canEdit ? (
            <Th align="right" sticky>
              {t('common.actions')}
            </Th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((grant) => (
          <tr key={grant.id}>
            {/* Truncated on purpose: the full hash is neither readable nor useful
                to a human, and the search box is how you find a specific one. */}
            <Td mono>{grant.deviceHash.slice(0, 12)}…</Td>
            <Td>{grant.userEmail ?? <span className="text-faint">{grant.userId.slice(0, 8)}…</span>}</Td>
            <Td align="right" mono>
              {usd(grant.amountCents)}
            </Td>
            <Td align="right" mono strong>
              {usd(grant.balanceCents)}
            </Td>
            <Td>{grant.platform ?? '—'}</Td>
            <Td mono>{new Date(grant.grantedAt).toLocaleDateString(locale)}</Td>
            <Td mono>
              {grant.expiresAt ? (
                new Date(grant.expiresAt).toLocaleDateString(locale)
              ) : (
                <span className="text-faint">{t('promotions.never')}</span>
              )}
            </Td>
            <Td>
              <Badge tone={grant.status === 'granted' ? 'success' : 'danger'}>
                {t(grant.status === 'granted' ? 'promotions.granted' : 'promotions.revoked')}
              </Badge>
            </Td>
            {canEdit ? (
              <Td align="right" sticky>
                {grant.status === 'granted' ? (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (window.confirm(t('promotions.revokeConfirm'))) revoke.mutate(grant.id);
                    }}
                  >
                    {t('promotions.revoke')}
                  </Button>
                ) : null}
              </Td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
